import { InformationCircleIcon } from "@heroicons/react/20/solid";
import {
  ActionIcon, Collapse,
  Divider,
  Group,
  Stack, Text, Tooltip
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown } from "@tabler/icons-react";
import SmallTextWithCopy from "../../../../../shared/copy/SmallText";
import Dash from "../../../../../shared/placeholder/Dash";
import Pill from "../../../../../shared/ui/badges/Pill";
import ProfileImg from "../../../../../shared/ui/icons/ProfileImg";
import TruncatedTextTooltip from "../../../../../shared/ui/truncate/TruncatedText";
import { dateFormat } from "../../../../../utils/tableUtils";
import IntegratedSources from "../../components/IntegratedSources";
import AccountInfo from "./AccountInfo";
import UserTraits from "./UserTraits";

type Props = {
  user: any,
};

const UserProfileHeader = ({ user }: Props) => {
  const [accountOpen, { toggle: toggleAccount }] = useDisclosure(true);
  const [sourcesOpen, { toggle: toggleSources }] = useDisclosure(true);

  const hasConnectedSources = Boolean(
    user?.hubspot_record_id ||
    user?.salesforce_record_id
  );
  return (
    <Stack align="center" gap="sm" className="w-full p-4">
      <ProfileImg
        width={50}
        height={50}
        desc={user?.email}
        img={user?.avatar_url}
        name={user?.name}
      />
      <Stack gap={2} align="center" className="w-full">
        {user?.name && (
          <TruncatedTextTooltip fw={600} width={200} ta="center" size="lg" c="text.9" tooltipText={user?.name}>
            {user?.name}
            {user?.title && <Pill color="purple" rounded size="xs" text={user.title} className="ml-1" />}
          </TruncatedTextTooltip>
        )}
        {user?.id && <SmallTextWithCopy size="xs" width={150} text={user.id} />}
        {user?.email && (
          <TruncatedTextTooltip width={200} ta="center" tooltipText={user.email} c="text.5" size="sm">
            {user.email}
          </TruncatedTextTooltip>
        )}
      </Stack>
      <div className="bg-gray-50 rounded p-2 my-2 w-full">
        <Stack gap={6} className="w-full">
          {user?.created_at && (
            <div className="flex items-center justify-between">
              <Text c="text.6" size="xs" className="font-semibold">Joined On</Text>
              <Text c="text.6" size="xs" className="font-semibold">{dateFormat("date", user.created_at)}</Text>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Text c="text.6" size="xs" className="font-semibold">Last Seen</Text>
              <Tooltip withinPortal label="The last time we received an event for this User" position="top" multiline maw={200}>
                <InformationCircleIcon className="w-3 h-3 cursor-help hover:text-gray-500 text-gray-400" />
              </Tooltip>
            </div>
            {user?.last_seen ? (
              <Text c="text.6" size="xs" className="font-semibold">{dateFormat("last", user.last_seen)}</Text>
            ) : <Dash />}
          </div>
        </Stack>
      </div>

      {/* Connected Data Sources */}
      {hasConnectedSources && (
        <>
          <Group justify="space-between" className="w-full" align="center">
            <Divider label="External Links" labelPosition="left" style={{ flex: 1 }} />
            <ActionIcon variant="subtle" onClick={toggleSources} aria-label={sourcesOpen ? 'Collapse' : 'Expand'}>
              <IconChevronDown size={16} style={{ transform: sourcesOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }} />
            </ActionIcon>
          </Group>
          <Collapse in={sourcesOpen}>
            <IntegratedSources account={user} />
          </Collapse>
        </>
      )}

      {/* User Traits Section */}
      <UserTraits contact={user} />

      {/* Related Accounts Section */}
      {
        user?.account_id && (
          <div className="w-full">
            <Group justify="space-between" className="w-full" align="center">
              <Divider className="flex-1" label="Related Accounts" labelPosition="left" />
              <ActionIcon variant="subtle" onClick={toggleAccount} aria-label={accountOpen ? 'Collapse' : 'Expand'}>
                <IconChevronDown size={16} style={{ transform: accountOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }} />
              </ActionIcon>
            </Group>
            <Collapse in={accountOpen}>
              <AccountInfo accountIds={user?.related_accounts} />
            </Collapse>
          </div>
        )
      }
    </Stack>
  );
};

export default UserProfileHeader;
